import React, { useEffect, useState } from "react";
import Loader from "../../../components/Loader/Loader";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import Alert from "../../../components/v3/Alert";
import Empty from "../../../components/v3/Empty";
import Field from "../../../components/v3/Field";
import Btn from "../../../components/v3/Btn";
import Pill from "../../../components/v3/Pill";
import { drApi } from "../../../services/drApi";
import { formatAuditTimestamp } from "../../../utils/fmtDate";

const CATEGORY_OPTIONS = [
  { value: "",                  label: "All categories" },
  { value: "KYC",               label: "KYC" },
  { value: "SUITABILITY",       label: "Suitability" },
  { value: "CLASSIFICATION",    label: "Classification" },
  { value: "INVESTOR_APPROVAL", label: "Investor approval" },
  { value: "DEAL_APPROVAL",     label: "Deal approval" },
  { value: "CREDENTIAL",        label: "Credential" },
  { value: "ROLE",              label: "Role" },
  { value: "SYSTEM",            label: "System" },
];

function DRAudit() {
  const [filters, setFilters] = useState({ category: "", dateFrom: "", dateTo: "" });
  const [appliedFilters, setAppliedFilters] = useState({ category: "", dateFrom: "", dateTo: "" });
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    const params = { page, limit };
    if (appliedFilters.category) params.category = appliedFilters.category;
    if (appliedFilters.dateFrom) params.dateFrom = appliedFilters.dateFrom;
    if (appliedFilters.dateTo)   params.dateTo   = appliedFilters.dateTo;

    drApi
      .audit(params)
      .then((res) => {
        if (!live) return;
        const d = res?.data || {};
        setRows(Array.isArray(d.rows) ? d.rows : []);
        setTotal(d.total || 0);
        setTotalPages(d.total_pages || 0);
        setError(null);
      })
      .catch((err) => {
        if (!live) return;
        setError(err?.response?.data?.message || err.message || "Fetch failed");
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [appliedFilters, page, limit]);

  function applyFilters(e) {
    e.preventDefault();
    setAppliedFilters(filters);
    setPage(1);
  }

  function resetFilters() {
    const cleared = { category: "", dateFrom: "", dateTo: "" };
    setFilters(cleared);
    setAppliedFilters(cleared);
    setPage(1);
  }

  const subtitle = (
    <>
      <span className="mono">{total}</span>
      <span>entr{total === 1 ? "y" : "ies"}</span>
      <span>·</span>
      <span className="mono">page {page} of {Math.max(totalPages, 1)}</span>
    </>
  );

  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead title="My audit trail" subtitle={subtitle} />

      <p style={{ color: "var(--tx-3)", fontSize: "var(--t-sm)", margin: "0 0 12px" }}>
        Every mutating action you perform is captured here. Scope is limited to your own actions — AD oversees platform-wide audit.
      </p>

      <section className="card">
        <div className="card__body">
          <form onSubmit={applyFilters} style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
            <div style={{ minWidth: 200 }}>
              <Field
                as="select"
                label="Category"
                name="category"
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              >
                {CATEGORY_OPTIONS.map((o) => (
                  <option key={o.value || "all"} value={o.value}>{o.label}</option>
                ))}
              </Field>
            </div>
            <div style={{ minWidth: 160 }}>
              <Field
                label="From"
                name="dateFrom"
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              />
            </div>
            <div style={{ minWidth: 160 }}>
              <Field
                label="To"
                name="dateTo"
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              />
            </div>
            <Btn variant="primary" type="submit">Apply</Btn>
            <Btn variant="ghost" type="button" onClick={resetFilters}>Reset</Btn>
          </form>
        </div>
      </section>

      {error && <Alert tone="danger">{error}</Alert>}

      {rows.length === 0 ? (
        <Empty>No audit entries match these filters.</Empty>
      ) : (
        <section className="card">
          <div className="card__body card__body--flush">
            <table className="tbl">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Category</th>
                  <th>Action</th>
                  <th>Target</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="mono">{formatAuditTimestamp(r.performed_at)}</td>
                    <td>
                      <Pill tone="blue">{r.action_category}</Pill>
                    </td>
                    <td>{r.action_type}</td>
                    <td>
                      <div className="muted">{r.target_entity_type}</div>
                      <div className="tbl-id">#{r.target_entity_id}</div>
                    </td>
                    <td className="muted" style={{ whiteSpace: "normal", maxWidth: 320 }}>
                      {r.notes || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {totalPages > 1 && (
        <div className="toolbar">
          <Btn size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Btn>
          <span className="mono" style={{ fontSize: "var(--t-sm)", color: "var(--tx-3)" }}>
            Page {page} of {totalPages}
          </span>
          <Btn size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</Btn>
        </div>
      )}
    </PageShell>
  );
}

export default DRAudit;
